import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { FiAlertTriangle, FiX } from "react-icons/fi";

import styles from "./DeleteAccountModal.module.css";

// Confirmation modal for permanent account deletion. Spells out exactly what will be destroyed and
// gates the action behind typing a confirmation word, so it can't be triggered by a stray click.
const DeleteAccountModal = ({ isOpen, loading, error, onCancel, onConfirm }) => {
  const { t } = useTranslation();
  const [typed, setTyped] = useState("");

  const confirmWord = t("settings.deleteAccount.confirmWord");

  useEffect(() => {
    if (isOpen) setTyped("");
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e) => {
      if (e.key === "Escape" && !loading) onCancel?.();
    };
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [isOpen, loading, onCancel]);

  if (!isOpen) return null;

  const cancel = () => {
    if (!loading) onCancel?.();
  };

  const canConfirm =
    !loading && typed.trim().toLowerCase() === confirmWord.trim().toLowerCase();

  const rawItems = t("settings.deleteAccount.items", { returnObjects: true });
  const items = Array.isArray(rawItems) ? rawItems : [];

  return (
    <div className={styles.overlay} onMouseDown={cancel}>
      <div
        className={styles.modal}
        role="dialog"
        aria-modal="true"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className={styles.header}>
          <span className={styles.headerIcon}>
            <FiAlertTriangle size={22} />
          </span>
          <h2 className={styles.title}>{t("settings.deleteAccount.modalTitle")}</h2>
          <button type="button" className={styles.closeBtn} onClick={cancel} aria-label={t("common.close")}>
            <FiX size={20} />
          </button>
        </div>

        <div className={styles.body}>
          <p className={styles.intro}>{t("settings.deleteAccount.modalIntro")}</p>
          <ul className={styles.list}>
            {items.map((item, i) => (
              <li key={i}>{item}</li>
            ))}
          </ul>
          <p className={styles.warning}>{t("settings.deleteAccount.irreversible")}</p>

          <label className={styles.confirmLabel} htmlFor="delete-account-confirm">
            {t("settings.deleteAccount.confirmPrompt", { word: confirmWord })}
          </label>
          <input
            id="delete-account-confirm"
            type="text"
            className={styles.confirmInput}
            value={typed}
            onChange={(e) => setTyped(e.target.value)}
            placeholder={confirmWord}
            autoComplete="off"
            disabled={loading}
          />

          {error && (
            <div className={styles.error} role="alert">
              {error}
            </div>
          )}
        </div>

        <div className={styles.actions}>
          <button type="button" className={styles.cancelBtn} onClick={cancel} disabled={loading}>
            {t("common.cancel")}
          </button>
          <button type="button" className={styles.confirmBtn} onClick={onConfirm} disabled={!canConfirm}>
            {loading ? t("settings.deleteAccount.deleting") : t("settings.deleteAccount.confirmButton")}
          </button>
        </div>
      </div>
    </div>
  );
};

export default DeleteAccountModal;
